(function (globalScope) {
  'use strict';

  const STORAGE_KEY = 'yg-discount-quantity-limit';
  const CHECKOUT_DISCOUNT_KEY = 'yg-cart-discount-code';
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update)(\.js)?(?:\?|$)/;

  function getCookieValue(cookieString, name) {
    const encodedName = `${encodeURIComponent(name)}=`;
    const cookie = String(cookieString || '')
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(encodedName));

    return cookie ? decodeURIComponent(cookie.slice(encodedName.length)) : '';
  }

  function shouldCaptureLanding({ discountCode, productId, redirectCount }) {
    return Boolean(discountCode && productId && Number(redirectCount) > 0);
  }

  function getProductQuantity(cart, productId) {
    return Array.isArray(cart?.items)
      ? cart.items.reduce((quantity, item) => {
          return String(item.product_id) === String(productId) ? quantity + Number(item.quantity || 0) : quantity;
        }, 0)
      : 0;
  }

  function canAddTargetProduct(cartQuantity, requestedQuantity) {
    return Number(cartQuantity) + Number(requestedQuantity) <= 1;
  }

  function getTargetLineKeys(cart, productId) {
    return Array.isArray(cart?.items)
      ? cart.items
          .filter((item) => String(item.product_id) === String(productId))
          .map((item) => item.key)
          .filter(Boolean)
      : [];
  }

  function getNavigationRedirectCount() {
    const navigationEntry = globalScope.performance?.getEntriesByType?.('navigation')?.[0];
    return Number(navigationEntry?.redirectCount || globalScope.performance?.navigation?.redirectCount || 0);
  }

  function parseVariantIds(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
      return [];
    }
  }

  function getRequestItems(body) {
    if (!body) return [];

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return [{ id: body.get('id'), quantity: Number(body.get('quantity') || 1) }];
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return [{ id: body.get('id'), quantity: Number(body.get('quantity') || 1) }];
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (Array.isArray(parsed.items)) return parsed.items;
        return [{ id: parsed.id, quantity: Number(parsed.quantity || 1) }];
      } catch (error) {
        const params = new URLSearchParams(body);
        return [{ id: params.get('id'), quantity: Number(params.get('quantity') || 1) }];
      }
    }

    return [];
  }

  const exportedUtilities = {
    canAddTargetProduct,
    getCookieValue,
    getProductQuantity,
    getTargetLineKeys,
    shouldCaptureLanding,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = exportedUtilities;
  }

  if (!globalScope.document || !globalScope.customElements) return;

  class YGDiscountQuantityLimit extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.cartPromise = null;
      this.rule = null;
      this.originalFetch = globalScope.ygDiscountQuantityLimitOriginalFetch || globalScope.fetch.bind(globalScope);
      this.handleCartUpdated = this.handleCartUpdated.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleQuantityChange = this.handleQuantityChange.bind(this);
    }

    connectedCallback() {
      this.rule = this.getStoredRule();
      this.captureLandingRule();
      if (!this.rule) return;

      this.installFetchGuard();
      document.addEventListener('cart:updated', this.handleCartUpdated);
      document.addEventListener('click', this.handleClick, true);
      document.addEventListener('change', this.handleQuantityChange, true);
      this.getCart().then((cart) => this.syncControls(cart)).catch(() => {});
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.handleCartUpdated);
      document.removeEventListener('click', this.handleClick, true);
      document.removeEventListener('change', this.handleQuantityChange, true);
    }

    getStoredRule() {
      try {
        return JSON.parse(globalScope.sessionStorage.getItem(STORAGE_KEY));
      } catch (error) {
        return null;
      }
    }

    captureLandingRule() {
      const discountCode = getCookieValue(document.cookie, 'discount_code');
      const productId = this.dataset.productId;
      const redirectCount = getNavigationRedirectCount();

      if (!shouldCaptureLanding({ discountCode, productId, redirectCount })) return;

      this.rule = {
        discountCode,
        productId: String(productId),
        variantIds: parseVariantIds(this.dataset.variantIds),
      };
      globalScope.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.rule));
      localStorage.setItem(CHECKOUT_DISCOUNT_KEY, discountCode);
    }

    installFetchGuard() {
      globalScope.ygDiscountQuantityLimit = this;
      if (globalScope.fetch.ygDiscountQuantityLimitInstalled) return;

      globalScope.ygDiscountQuantityLimitOriginalFetch = this.originalFetch;
      const guardedFetch = async (input, init = {}) => {
        const controller = globalScope.ygDiscountQuantityLimit;
        const url = typeof input === 'string' ? input : input?.url || '';
        const isAddRequest = /\/cart\/add(\.js)?(?:\?|$)/.test(url);
        let hasTargetProduct = false;

        if (controller?.rule && isAddRequest) {
          const items = getRequestItems(init.body);
          const requestedQuantity = items.reduce((quantity, item) => {
            return controller.rule.variantIds.includes(String(item.id))
              ? quantity + Number(item.quantity || 1)
              : quantity;
          }, 0);
          hasTargetProduct = requestedQuantity > 0;
        }

        const response = await globalScope.ygDiscountQuantityLimitOriginalFetch(input, init);
        if (controller?.rule && hasTargetProduct && response.ok) {
          await controller.replaceTargetProduct(response.clone());
        }
        if (controller?.rule && CART_MUTATION_PATTERN.test(url) && response.ok) controller.scheduleCartSync();
        return response;
      };

      guardedFetch.ygDiscountQuantityLimitInstalled = true;
      globalScope.fetch = guardedFetch;
    }

    async getCart(forceRefresh = false) {
      if (forceRefresh && this.cartPromise) {
        try {
          await this.cartPromise;
        } catch (error) {
          this.cart = null;
        }
      }
      if (this.cart && !forceRefresh) return this.cart;
      if (this.cartPromise) return this.cartPromise;

      this.cartPromise = this.originalFetch(`${globalScope.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load cart');
          return response.json();
        })
        .then((cart) => {
          this.cart = cart;
          return cart;
        })
        .finally(() => {
          this.cartPromise = null;
        });

      return this.cartPromise;
    }

    async replaceTargetProduct(addResponse) {
      const addedPayload = await addResponse.json();
      const addedItems = Array.isArray(addedPayload.items) ? addedPayload.items : [addedPayload];
      const addedTargetItem = addedItems.find((item) => {
        return String(item.product_id) === this.rule.productId
          || this.rule.variantIds.includes(String(item.variant_id || item.id));
      });
      if (!addedTargetItem?.key) return;

      const cart = await this.getCart(true);
      const updates = {};
      getTargetLineKeys(cart, this.rule.productId).forEach((lineKey) => {
        updates[lineKey] = lineKey === addedTargetItem.key ? 1 : 0;
      });

      const response = await this.originalFetch(`${globalScope.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('Unable to replace promotional cart item');
      this.cart = await response.json();
    }

    scheduleCartSync() {
      globalScope.setTimeout(() => {
        this.getCart(true).then((cart) => this.syncControls(cart)).catch(() => {});
      }, 0);
    }

    handleCartUpdated(event) {
      if (!event.detail?.cart) return;
      this.cart = event.detail.cart;
      this.syncControls(this.cart);
    }

    handleClick(event) {
      const increaseButton = event.target.closest('[data-qty-change="inc"]');
      if (!increaseButton || !this.isTargetProductElement(increaseButton)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.showMessage(increaseButton);
    }

    handleQuantityChange(event) {
      const input = event.target;
      if (!input.matches('input[name="quantity"], input[name="updates[]"]')) return;
      if (!this.isTargetProductElement(input) || Number(input.value) <= 1) return;

      event.stopImmediatePropagation();
      input.value = '1';
      this.showMessage(input);
    }

    isTargetProductElement(element) {
      return String(element.closest('[data-product-id]')?.dataset.productId || '') === this.rule.productId;
    }

    syncControls(cart) {
      const cartQuantity = getProductQuantity(cart, this.rule.productId);
      const selector = `[data-product-id="${CSS.escape(this.rule.productId)}"]:not(body)`;

      document.querySelectorAll(selector).forEach((container) => {
        container.querySelectorAll('input[name="quantity"], input[name="updates[]"], .scd-item__qty_input').forEach((input) => {
          input.max = '1';
          if (Number(input.value) > 1) input.value = '1';
        });

        container.querySelectorAll('[data-qty-change="inc"]').forEach((button) => {
          button.disabled = cartQuantity >= 1;
          button.setAttribute('aria-disabled', String(cartQuantity >= 1));
        });
      });
    }

    createLimitResponse() {
      this.showMessage(document.body);
      return new Response(
        JSON.stringify({ status: 422, message: 'Cart Error', description: this.dataset.message }),
        { status: 422, headers: { 'Content-Type': 'application/json' } },
      );
    }

    showMessage(target) {
      const notificationTarget = target.closest?.('.product-form, [data-cart-item]') || document.body;
      if (globalScope.MinimogTheme?.Notification?.show) {
        globalScope.MinimogTheme.Notification.show({
          target: notificationTarget,
          method: 'appendChild',
          type: 'warning',
          message: this.dataset.message,
          last: 3000,
        });
        return;
      }

      const status = this.querySelector('[data-limit-status]');
      status.hidden = false;
      status.textContent = this.dataset.message;
    }
  }

  if (!globalScope.customElements.get('yg-discount-quantity-limit')) {
    globalScope.customElements.define('yg-discount-quantity-limit', YGDiscountQuantityLimit);
  }
})(typeof window !== 'undefined' ? window : globalThis);
